import { Box, Stack, Tab, Tabs, Typography } from "@mui/material"
import { Outlet, NavLink, useLocation } from "react-router-dom"
import { TabsStyle, TypTitle } from "./style"
import { IProjectResponse } from "../../../api/types"
import { useGetProjectUserMatchesQuery } from "../../../api/recommendations"
import { COLORS } from "../../../styles/variables"

interface ProjectTabsProps {
  project: IProjectResponse
}

export const ProjectTabs = ({ project }: ProjectTabsProps) => {
  const location = useLocation()
  const canViewTeamSpace = project.viewer?.permissions?.can_view_team_space === true
  const canEditContent = project.viewer?.permissions?.can_edit_content === true

  const { data: matchesPreview, isError: isMatchesError } = useGetProjectUserMatchesQuery(
    { projectId: project.id, limit: 1 },
    { skip: !canEditContent, refetchOnFocus: true },
  )

  const hasProjectMatches =
    !isMatchesError &&
    (
      (matchesPreview?.exact?.length ?? 0) > 0
      || (matchesPreview?.potential?.length ?? 0) > 0
      || Boolean(matchesPreview?.meta?.next_cursor)
    )

  const currentTab = (() => {
    if (canEditContent && location.pathname.includes("recommendations")) {
      return 0
    }
    if (location.pathname.includes("team-space")) {
      if (!canViewTeamSpace) return canEditContent ? 1 : 0
      return canEditContent ? 4 : 3
    }
    if (location.pathname.includes("actionplan")) {
      return canEditContent ? 2 : 1
    }
    if (location.pathname.includes("needs")) {
      return canEditContent ? 3 : 2
    }
    // Описание
    return canEditContent ? 1 : 0
  })()

  const needsSubTab = location.pathname.includes("whatneeded") && location.pathname.includes("needs")
    ? 1
    : location.pathname.includes("whoavailable")
      ? 2
      : location.pathname.includes("whatavailable")
        ? 3
        : 0

  const recommendationsSubTab = location.pathname.includes("recommendations")
    && location.pathname.includes("whatneeded")
    ? 1
    : 0

  const recommendationsLabel = (
    <Stack direction="row" component="span" alignItems="center" gap={0.75}>
      <Box component="span">Рекомендации</Box>
      {hasProjectMatches ? (
        <Box
          component="span"
          aria-hidden
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: COLORS.CADMIUM_RED,
            flexShrink: 0,
          }}
        />
      ) : null}
    </Stack>
  )

  return (
    <Stack height={{ xs: "100%", md: "auto" }}>
      <Typography variant="h1" sx={TypTitle}>{project.name}</Typography>
      <Stack direction="column" gap={{ xs: 1.5, sm: 2 }} mb={{ xs: 2.5, md: 5 }}>
        <Tabs
          value={currentTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={TabsStyle}
        >
          {canEditContent ? (
            <Tab
              label={recommendationsLabel}
              component={NavLink}
              to="recommendations/whoneeded"
            />
          ) : null}
          <Tab label="Описание" component={NavLink} to="" end />
          <Tab label="План действий" component={NavLink} to="actionplan" />
          <Tab label="Нам нужны" component={NavLink} to="needs/whoneeded" />
          {canViewTeamSpace ? (
            <Tab label="Команда" component={NavLink} to="team-space" />
          ) : null}
        </Tabs>
        {canEditContent && location.pathname.includes("recommendations") ? (
          <Tabs
            value={recommendationsSubTab}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={TabsStyle}
          >
            <Tab label="Кто нужен" component={NavLink} to="recommendations/whoneeded" />
            <Tab label="Что нужно" component={NavLink} to="recommendations/whatneeded" />
          </Tabs>
        ) : null}
        {location.pathname.includes("needs") && (
          <Tabs
            value={needsSubTab}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={TabsStyle}
          >
            <Tab label="Кто нужен" component={NavLink} to="needs/whoneeded" />
            <Tab label="Что нужно" component={NavLink} to="needs/whatneeded" />
            <Tab label="Кто уже есть" component={NavLink} to="needs/whoavailable" />
            <Tab label="Что уже есть" component={NavLink} to="needs/whatavailable" />
          </Tabs>
        )}
      </Stack>
      <Outlet context={project} />
    </Stack>
  )
}
